import React, { useState, useEffect } from 'react';
import './LinkPreview.css';

function LinkPreview({ url, onRemove }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchPreview();
  }, [url]);

  const fetchPreview = async () => {
    try {
      setLoading(true);
      setError(false);

      // First, try to get basic info from the URL itself
      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');

      // Set initial preview with basic info
      const basicPreview = {
        url: url,
        title: domain,
        siteName: domain,
        favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`,
        description: '',
        image: ''
      };

      // Check if it's a known site with special handling
      const specialPreview = getSpecialSitePreview(url, urlObj);
      if (specialPreview) {
        setPreview({ ...basicPreview, ...specialPreview });
        setLoading(false);
        return;
      }

      // Try multiple methods to get preview data
      let previewData = null;

      // Method 1: Try using a CORS proxy service for metadata
      try {
        const corsProxy = 'https://corsproxy.io/?';
        const proxyUrl = corsProxy + encodeURIComponent(url);

        const response = await fetch(proxyUrl, {
          method: 'GET',
          headers: {
            'Accept': 'text/html',
          },
          ...(AbortSignal.timeout ? { signal: AbortSignal.timeout(5000) } : {})
        });

        if (response.ok) {
          const html = await response.text();
          previewData = parseMetaTags(html, url);
        }
      } catch (corsError) {
        console.log('CORS proxy failed, trying alternative method');
      }

      // Method 2: Try backend endpoint as fallback
      if (!previewData) {
        try {
          const response = await fetch(`/api/link-preview?url=${encodeURIComponent(url)}`, {
            credentials: 'include',
            ...(AbortSignal.timeout ? { signal: AbortSignal.timeout(5000) } : {})
          });

          if (response.ok) {
            previewData = await response.json();
          }
        } catch (backendError) {
          console.log('Backend fetch failed');
        }
      }

      // Method 3: Use Open Graph API service (if available)
      if (!previewData) {
        try {
          // Using a public API that doesn't require authentication
          const apiUrl = `https://api.microlink.io?url=${encodeURIComponent(url)}`;
          const response = await fetch(apiUrl, {
            ...(AbortSignal.timeout ? { signal: AbortSignal.timeout(5000) } : {})
          });

          if (response.ok) {
            const data = await response.json();
            if (data.status === 'success' && data.data) {
              previewData = {
                title: data.data.title || domain,
                description: data.data.description || '',
                image: data.data.image?.url || '',
                favicon: data.data.logo?.url || basicPreview.favicon,
                siteName: data.data.publisher || domain,
                url: url
              };
            }
          }
        } catch (apiError) {
          console.log('API fetch failed');
        }
      }

      // Use the preview data if we got any, otherwise use basic preview
      setPreview(previewData || basicPreview);

    } catch (err) {
      console.error('Error fetching link preview:', err);

      // Fallback to basic preview
      try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.replace('www.', '');

        setPreview({
          url: url,
          title: domain,
          siteName: domain,
          favicon: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`,
          description: '',
          image: ''
        });
      } catch {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const parseMetaTags = (html, url) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const urlObj = new URL(url);

    const getMetaContent = (property, name) => {
      return doc.querySelector(`meta[property="${property}"]`)?.getAttribute('content') ||
             doc.querySelector(`meta[name="${name}"]`)?.getAttribute('content') ||
             '';
    };

    const title = getMetaContent('og:title', 'twitter:title') ||
                  doc.querySelector('title')?.textContent ||
                  urlObj.hostname;

    const description = getMetaContent('og:description', 'twitter:description') ||
                       doc.querySelector('meta[name="description"]')?.getAttribute('content') ||
                       '';

    let image = getMetaContent('og:image', 'twitter:image');
    if (image && !image.startsWith('http')) {
      image = new URL(image, url).href;
    }

    const siteName = getMetaContent('og:site_name', '') || urlObj.hostname;

    return {
      url,
      title: title.substring(0, 100),
      description: description.substring(0, 200),
      image,
      favicon: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`,
      siteName
    };
  };

  const getSpecialSitePreview = (url, urlObj) => {
    const domain = urlObj.hostname.replace('www.', '');

    // YouTube
    if (domain.includes('youtube.com') || domain.includes('youtu.be')) {
      const videoId = extractYouTubeId(url);
      if (videoId) {
        return {
          title: 'YouTube Video',
          description: 'Watch on YouTube',
          image: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
          siteName: 'YouTube'
        };
      }
    }

    // Twitter/X
    if (domain.includes('twitter.com') || domain.includes('x.com')) {
      return {
        title: 'Post on X',
        description: 'View this post on X (formerly Twitter)',
        siteName: 'X'
      };
    }

    // GitHub
    if (domain.includes('github.com')) {
      const parts = urlObj.pathname.split('/').filter(Boolean);
      if (parts.length >= 2) {
        return {
          title: `${parts[0]}/${parts[1]}`,
          description: 'GitHub Repository',
          siteName: 'GitHub'
        };
      }
    }

    // LinkedIn
    if (domain.includes('linkedin.com')) {
      return {
        title: 'LinkedIn',
        description: 'View on LinkedIn',
        siteName: 'LinkedIn'
      };
    }

    return null;
  };

  const extractYouTubeId = (url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  if (loading) {
    return (
      <div className="link-preview loading">
        <div className="link-preview-spinner"></div>
        <span>Loading preview...</span>
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="link-preview error">
        <div className="link-preview-content">
          <div className="link-preview-url">{url}</div>
        </div>
        {onRemove && (
          <button className="link-preview-remove" onClick={onRemove}>×</button>
        )}
      </div>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="link-preview-anchor">
      <div className="link-preview">
        {preview.image && (
          <div className="link-preview-image">
            <img
              src={preview.image}
              alt={preview.title}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.style.display = 'none';
              }}
            />
          </div>
        )}
        <div className="link-preview-content">
          {preview.title && (
            <div className="link-preview-title">{preview.title}</div>
          )}
          {preview.description && (
            <div className="link-preview-description">{preview.description}</div>
          )}
          <div className="link-preview-url">
            {preview.favicon && (
              <img
                src={preview.favicon}
                alt=""
                className="link-preview-favicon"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <span>{preview.siteName || new URL(url).hostname}</span>
          </div>
        </div>
        {onRemove && (
          <button
            className="link-preview-remove"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
          >
            ×
          </button>
        )}
      </div>
    </a>
  );
}

export default LinkPreview;